import { z } from "zod";
import { Operation } from "./operations.js";
import { Labels } from "./ssh-keys.js";

/*
 * Volumes: network block storage of a fixed size in one location, attached
 * to at most one server of that location at a time. Billed per GB and month
 * for the whole size, attached or not; a volume can grow but never shrink.
 */

/** Smallest and largest volume the provider accepts (Hetzner: 10 GB … 10 TB). */
export const VOLUME_MIN_GB = 10;
export const VOLUME_MAX_GB = 10_240;
/** Volumes one server may have attached at once (Hetzner's limit). */
export const MAX_VOLUMES_PER_SERVER = 16;

/** File systems the provider can create on a new volume; null leaves it empty. */
export const VolumeFormat = z.enum(["ext4", "xfs"]);
export type VolumeFormat = z.infer<typeof VolumeFormat>;

export const VolumeStatus = z.enum(["creating", "available"]);
export type VolumeStatus = z.infer<typeof VolumeStatus>;

/**
 * Letters, digits, dots, hyphens and underscores, starting and ending with a
 * letter or digit. Stricter than the provider may be, so a name never fails there.
 */
export const VolumeName = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?$/, "Letters, digits, dots, hyphens");

export const VolumeSizeGb = z.number().int().min(VOLUME_MIN_GB).max(VOLUME_MAX_GB);

export const Volume = z.object({
  id: z.uuid(),
  name: z.string(),
  sizeGb: z.number().int(),
  region: z.string(),
  zone: z.string(),
  /** The file system the provider created; null when the volume was left empty. */
  format: VolumeFormat.nullable(),
  /** Where the server sees the disk, e.g. `/dev/disk/by-id/scsi-0HC_Volume_42`. */
  linuxDevice: z.string(),
  status: VolumeStatus,
  /** The server it is attached to, null when detached (filled in by the API). */
  server: z.object({ id: z.uuid().nullable(), name: z.string() }).nullable(),
  /** The provider's id of that server, as stored in the resource copy. */
  serverRef: z.string().nullable(),
  protection: z.object({ delete: z.boolean() }),
  labels: Labels,
  createdAt: z.iso.datetime(),
});
export type Volume = z.infer<typeof Volume>;

export const CreateVolumeRequest = z
  .object({
    name: VolumeName,
    sizeGb: VolumeSizeGb,
    /** Where the volume lives; taken from the server when `serverId` is given. */
    region: z.string().min(1).max(64).optional(),
    /** Attaches the new volume to this server right away. */
    serverId: z.uuid().optional(),
    format: VolumeFormat.nullable().default(null),
    /** Mount it on the server after attaching (needs a server and a file system). */
    automount: z.boolean().default(false),
    labels: Labels.default({}),
  })
  .refine((v) => v.region !== undefined || v.serverId !== undefined, {
    message: "Choose a region or a server",
    path: ["region"],
  })
  .refine((v) => !v.automount || (v.serverId !== undefined && v.format !== null), {
    message: "Mounting needs a server and a file system",
    path: ["automount"],
  });
export type CreateVolumeRequest = z.input<typeof CreateVolumeRequest>;

export const UpdateVolumeRequest = z
  .object({ name: VolumeName.optional(), labels: Labels.optional() })
  .refine((v) => v.name !== undefined || v.labels !== undefined, "Nothing to change");
export type UpdateVolumeRequest = z.infer<typeof UpdateVolumeRequest>;

export const AttachVolumeRequest = z.object({
  serverId: z.uuid(),
  automount: z.boolean().default(false),
});
export type AttachVolumeRequest = z.input<typeof AttachVolumeRequest>;

export const ResizeVolumeRequest = z.object({ sizeGb: VolumeSizeGb });
export type ResizeVolumeRequest = z.infer<typeof ResizeVolumeRequest>;

export const VolumeProtectionRequest = z.object({ delete: z.boolean() });

/** Answer of writes the provider finishes asynchronously. */
export const VolumeChange = z.object({ volume: Volume, operation: Operation });
export type VolumeChange = z.infer<typeof VolumeChange>;

/** Monthly price of a volume: its whole size times the price per GB-month. */
export function volumeMonthly(sizeGb: number, perGbMonth: string): number {
  return sizeGb * Number(perGbMonth);
}
